export type FractionMode = 'fraction' | 'ratio' | 'percent';
export type Rational = { numerator: bigint; denominator: bigint };
export type FractionStep = {
  title: string;
  formula: string;
  explanation: string;
  focus: 'parts' | 'decimal' | 'percent' | 'amount' | 'ratio';
};
export function fractionGcd(a: bigint, b: bigint): bigint {
  while (b) [a, b] = [b, a % b];
  return a < 0n ? -a : a;
}
export function rational(a: bigint, b: bigint): Rational {
  if (b <= 0n || a < 0n)
    throw new Error('This view needs non-negative parts and a positive denominator.');
  const gcd = fractionGcd(a, b);
  return { numerator: a / gcd, denominator: b / gcd };
}
export const rationalText = (value: Rational) =>
  value.denominator === 1n ? String(value.numerator) : `${value.numerator}/${value.denominator}`;
export const rationalNumber = (value: Rational) =>
  Number(value.numerator) / Number(value.denominator);
export function rationalDecimal(value: Rational) {
  const { numerator: a, denominator: b } = value;
  const integer = a / b;
  let rest = a % b,
    digits = '';
  const seen = new Map<bigint, number>();
  while (rest && digits.length < 60 && !seen.has(rest)) {
    seen.set(rest, digits.length);
    rest *= 10n;
    digits += String(rest / b);
    rest %= b;
  }
  if (!rest)
    return { text: `${integer}${digits ? '.' + digits : ''}`, exact: true, repeating: false };
  if (seen.has(rest)) {
    const start = seen.get(rest)!;
    return {
      text: `${integer}.${digits.slice(0, start)}(${digits.slice(start)})`,
      exact: true,
      repeating: true,
    };
  }
  return {
    text: rationalNumber(value).toLocaleString('en-AU', {
      maximumSignificantDigits: 8,
      useGrouping: false,
    }),
    exact: false,
    repeating: false,
  };
}
export function representation(value: Rational, suffix = '') {
  const decimal = rationalDecimal(value);
  return `${decimal.exact ? '=' : '≈'} ${decimal.text}${suffix}`;
}
function rationalForms(value: Rational) {
  const exact = rationalText(value),
    decimal = rationalDecimal(value);
  return decimal.exact && exact === decimal.text ? exact : `${exact} ${representation(value)}`;
}
function decimalInput(text: string, label: string, max: number): Rational {
  const trimmed = text.trim();
  if (!trimmed) throw new Error(`Enter ${label}. Your other inputs have been kept.`);
  const numeric = Number(trimmed);
  if (!Number.isFinite(numeric) || numeric < 0 || numeric > max)
    throw new Error(`${label} must be from 0 to ${max}. Your input has been kept.`);
  const match = (trimmed.startsWith('.') ? '0' + trimmed : trimmed).match(
    /^\+?(\d+)(?:\.(\d*))?(?:e([+-]?\d+))?$/i,
  );
  if (!match)
    throw new Error(`Enter ${label} as a non-negative number, with up to 6 decimal places.`);
  const places = (match[2]?.length ?? 0) - Number(match[3] ?? 0);
  if (places > 6)
    throw new Error(`${label} supports up to 6 decimal places. Your input has been kept.`);
  const coefficient = BigInt(match[1] + (match[2] ?? ''));
  // Range checking above bounds scientific notation before constructing a power of ten.
  if (places < -12) {
    if (coefficient === 0n) return rational(0n, 1n);
    throw new Error(`${label} exceeds this view's numeric range. Your input has been kept.`);
  }
  return places >= 0
    ? rational(coefficient, 10n ** BigInt(places))
    : rational(coefficient * 10n ** BigInt(-places), 1n);
}
function integerInput(text: string | undefined, label: string, min: number, max: number) {
  if (!(text ?? '').trim()) throw new Error(`Enter ${label}. Your other inputs have been kept.`);
  const value = Number(text);
  if (!Number.isInteger(value) || value < min || value > max)
    throw new Error(
      `${label} must be a whole number from ${min} to ${max}. Your input has been kept.`,
    );
  return value;
}
export function fractionExplorer(mode: FractionMode, draft: Record<string, string>) {
  let share: Rational,
    originalA: number,
    originalB: number,
    whole: Rational | null = null;
  let primaryLabel = 'Simplified fraction',
    primary = '';
  const steps: FractionStep[] = [];
  const add = (title: string, formula: string, explanation: string, focus: FractionStep['focus']) =>
    steps.push({ title, formula, explanation, focus });
  if (mode === 'fraction') {
    originalA = integerInput(draft.numerator, 'Numerator', 0, 1000);
    originalB = integerInput(draft.denominator, 'Denominator', 1, 1000);
    share = rational(BigInt(originalA), BigInt(originalB));
    const gcd = fractionGcd(BigInt(originalA), BigInt(originalB));
    add(
      'Read equal parts',
      `${originalA}/${originalB}: ${originalA} selected, ${originalB} equal parts per whole`,
      'The denominator describes equal-sized parts in one whole. The numerator counts how many of those parts are used.',
      'parts',
    );
    add(
      'Simplify the fraction',
      `${originalA}/${originalB} = (${originalA} ÷ ${gcd})/(${originalB} ÷ ${gcd}) = ${rationalText(share)}`,
      'Divide both numbers by their greatest common divisor. This changes the names of the parts, not the amount.',
      'parts',
    );
  } else if (mode === 'ratio') {
    originalA = integerInput(draft.a, 'Part A', 0, 1000);
    originalB = integerInput(draft.b, 'Part B', 0, 1000);
    if (originalA + originalB === 0)
      throw new Error(
        'A 0:0 ratio has no parts to share. Enter at least one positive part; your inputs have been kept.',
      );
    whole = decimalInput(draft.whole ?? '', 'Total amount', 1000000);
    share = rational(BigInt(originalA), BigInt(originalA + originalB));
    const gcd = fractionGcd(BigInt(originalA), BigInt(originalB));
    add(
      'Read the part-to-part ratio',
      `${originalA}:${originalB} = ${originalA / Number(gcd)}:${originalB / Number(gcd)}; total parts = ${originalA} + ${originalB} = ${originalA + originalB}`,
      'A:B compares the two parts. It does not mean that A is A/B of the whole.',
      'ratio',
    );
    add(
      'Find the share of the whole',
      `A/(A + B) = ${originalA}/(${originalA} + ${originalB}) = ${rationalText(share)}`,
      'Add both parts before finding the fraction of the whole. For 2:3, the first share is 2/5, not 2/3.',
      'parts',
    );
  } else {
    const percent = decimalInput(draft.percent ?? '', 'Percentage', 1000);
    whole = decimalInput(draft.whole ?? '', 'Whole amount', 1000000);
    share = rational(percent.numerator, percent.denominator * 100n);
    originalA = Number(share.numerator);
    originalB = Number(share.denominator);
    add(
      'Read percent as per hundred',
      `${rationalDecimal(percent).text}% = (${rationalDecimal(percent).text}) ÷ 100 = ${rationalText(share)}`,
      'Percent means per hundred. Dividing the percentage number by 100 gives the multiplier for one whole.',
      'percent',
    );
    add(
      'Write the fraction and decimal',
      `${rationalText(share)} ${representation(share)}`,
      'Simplify the fraction and divide its original numerator by its denominator. Never use a rounded decimal as the input for the next calculation.',
      'decimal',
    );
  }
  const decimal = rationalDecimal(share),
    percent = rational(share.numerator * 100n, share.denominator),
    percentDecimal = rationalDecimal(percent);
  if (mode !== 'percent') {
    add(
      'Divide to find the decimal',
      `${rationalText(share)} ${representation(share)}`,
      decimal.repeating
        ? 'Digits in parentheses repeat forever. This is an exact recurring decimal, not a rounded finite decimal.'
        : decimal.exact
          ? 'The division ends, so this decimal is exact.'
          : 'The full repeating cycle is longer than this view displays. The fraction stays exact; the decimal is marked as an approximation.',
      'decimal',
    );
    add(
      'Convert to percentage from the fraction',
      `Percentage number: (${share.numerator} × 100)/${share.denominator} = ${rationalForms(percent)}; percentage ${representation(percent, '%')}`,
      share.numerator === 4n && share.denominator === 9n
        ? 'Multiply the original fraction by 100. Do not use the rounded decimal 0.44: 0.44 × 100 gives 44, but (4/9 × 100)% is exactly 44.(4)%.'
        : 'Multiply the original numerator by 100, then divide by the denominator. A rounded display decimal must not become the input for this calculation.',
      'percent',
    );
  }
  const amount = whole
    ? rational(share.numerator * whole.numerator, share.denominator * whole.denominator)
    : null;
  let remainderAmount: Rational | null = null;
  if (mode === 'ratio' && whole && amount) {
    remainderAmount = rational(
      whole.numerator * amount.denominator - amount.numerator * whole.denominator,
      whole.denominator * amount.denominator,
    );
    const unit = rational(whole.numerator, whole.denominator * BigInt(originalA + originalB));
    add(
      'Allocate the total and check',
      `One part: ${rationalText(whole)} ÷ ${originalA + originalB} ${representation(unit)}; A = ${originalA} × (${rationalText(unit)}) ${representation(amount)}; B = ${originalB} × (${rationalText(unit)}) ${representation(remainderAmount)}; A + B = ${rationalText(whole)}`,
      'Multiply one ratio unit by A and by B. The exact shares add back to the entered total; separately rounded display amounts may not add perfectly.',
      'amount',
    );
    primaryLabel = 'Amount A';
    primary = `${rationalDecimal(amount).exact ? '' : '≈ '}${rationalDecimal(amount).text} units`;
  } else if (mode === 'percent' && whole && amount) {
    add(
      'Multiply by the whole amount',
      `${rationalText(whole)} × ${rationalText(share)} = ${rationalForms(amount)} units`,
      'Use the exact fraction multiplier. This is the selected amount, not the percentage number.',
      'amount',
    );
    add(
      'Check the scale of the result',
      `${percentDecimal.text}% of ${rationalText(whole)} ${representation(amount)} units`,
      whole.numerator === 0n
        ? 'A zero whole amount gives zero units. The bar still represents the chosen percentage multiplier, not a physical amount.'
        : share.numerator > share.denominator
          ? 'More than 100% means more than one whole, so the result exceeds the original whole amount.'
          : 'Between 0% and 100% gives an amount from zero to the whole. 100% gives the whole itself.',
      'amount',
    );
    primaryLabel = 'Selected amount';
    primary = `${rationalDecimal(amount).exact ? '' : '≈ '}${rationalDecimal(amount).text} units`;
  } else primary = rationalText(share);
  let partRest: string | null = null;
  if (share.numerator <= share.denominator) {
    const rest = share.denominator - share.numerator,
      gcd = fractionGcd(share.numerator, rest);
    partRest = `${share.numerator / gcd}:${rest / gcd}`;
  }
  const mixed =
    share.numerator > share.denominator && share.numerator % share.denominator !== 0n
      ? `${share.numerator / share.denominator} ${share.numerator % share.denominator}/${share.denominator}`
      : null;
  return {
    mode,
    share,
    originalA,
    originalB,
    whole,
    amount,
    remainderAmount,
    primaryLabel,
    primary,
    decimal,
    percent,
    percentDecimal,
    partRest,
    mixed,
    steps,
    fraction: rationalText(share),
    value: rationalNumber(share),
    task:
      mode === 'ratio'
        ? 'Keep the total fixed. Compare 2:3 with 3:2. Which share grows, and why do both still add to the same total?'
        : mode === 'percent'
          ? 'Try 25%, 100% and 125% of the same amount. Explain which answers are smaller than, equal to or larger than one whole.'
          : 'Compare 4/9 and 8/18. Explain why the fraction, point, recurring decimal and percentage describe the same quantity.',
  };
}
export type FractionResult = ReturnType<typeof fractionExplorer>;
